import { useObjectRef } from '@react-aria/utils';
import { createContext, forwardRef } from 'react';
import type { ContextValue } from 'react-aria-components';
import { Link as AriaLink, Provider } from 'react-aria-components';

import {
  cn,
  pressScale,
  useStyleProps,
  useContextProps,
} from '@infinex/ui-styles';
import { isReactText } from '@infinex/ui-utils/react';

import { getButtonContextValues } from './Button';
import { buttonVariants } from './buttonVariants';
import type { LinkButtonProps } from './types';

import { Text } from '../content';
import { useSurfaceLayer } from '../surface';

export const LinkButtonContext =
  createContext<ContextValue<LinkButtonProps, HTMLAnchorElement>>(null);

/**
 * Shares all the same `Button` cosmetics but with link semantics, allowing
 * users to navigate to another page.
 *
 * [Taurus docs: `LinkButton`](https://ui-sb.app.infinex.dev/?path=/docs/components-linkbutton--docs)
 */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(
  function LinkButton(props, ref) {
    [props, ref] = useContextProps(props, ref, LinkButtonContext);
    const domRef = useObjectRef<HTMLAnchorElement>(ref);
    const { className, style } = useStyleProps(props);
    const {
      children,
      prominence,
      size = 'medium',
      staticColor,
      ...otherProps
    } = props;

    const layer = useSurfaceLayer();
    const contextValues = getButtonContextValues(props);

    return (
      // @ts-expect-error — this type would be inferred if inline but we have to share button context values
      <Provider values={contextValues}>
        <AriaLink
          {...otherProps}
          className={cn(
            buttonVariants({
              // static colours must render the same on any background, so the
              // surface layer adaptations don't apply
              layer: staticColor ? undefined : layer,
              prominence,
              size,
              staticColor,
            }),
            className
          )}
          ref={domRef}
          style={pressScale(domRef, style)}
        >
          {isReactText(children) ? <Text>{children}</Text> : children}
        </AriaLink>
      </Provider>
    );
  }
);
